import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { getUserTotalWatchtimeForDisplay, getUserVideoStatsForDisplay, getUserAllVideoProgress } from '../../utils/videoUtils';
import { rtdb } from '../../config/firebase';
import { ref as rtdbRef, onValue } from 'firebase/database';
import { useAuth } from '../../contexts/AuthContext';
import { grantSubscription } from '../../utils/subscriptionService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  Typography,
  Avatar,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Card,
  CardContent,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import type { VideoStats } from '../../types/user';
import { useTranslation } from 'react-i18next';

interface User {
  id: string;
  name: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  platformLogins?: { booking?: number; video?: number };
  videoStats?: VideoStats;
  role?: string;
  createdAt?: any;
  lastLogin?: any;
  isActive?: boolean;
  subscription?: {
    active?: boolean;
    plan?: string;
    startDate?: any;
    endDate?: any;
    unlockedModules?: number[];
    history?: any[];
  };
  userTotalVideosStarted?: number; // Added for the new stat
  userTotalVideosCompleted?: number;
  totalWatchtime?: number; // Total watchtime in seconds
}

interface UserManagementDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
}

const UserManagementDialog: React.FC<UserManagementDialogProps> = ({ open, onClose, user }) => {
  const { t } = useTranslation();
  const { user: currentUser, role: currentRole } = useAuth();
  const isAdminOrTrainer = currentRole === 'admin' || currentRole === 'trainer';
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [videos, setVideos] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Editable user data
  const [editedUser, setEditedUser] = useState<User | null>(null);
  const [onlineStatus, setOnlineStatus] = useState<'online' | 'offline' | null>(null);
  const [userTotalWatchtime, setUserTotalWatchtime] = useState<number>(0);
  const [userVideoStats, setUserVideoStats] = useState<{ started: number; completed: number }>({ started: 0, completed: 0 });
  const [userVideoProgress, setUserVideoProgress] = useState<{ [videoId: string]: any }>({});
  const [progressLoading, setProgressLoading] = useState(false);

  // Subscription granting state
  const [grantMonths, setGrantMonths] = useState<number>(1);
  const [grantingSubscription, setGrantingSubscription] = useState(false);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Add filter state
  const [selectedModule, setSelectedModule] = useState<number | 'all'>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedVideoNumber, setSelectedVideoNumber] = useState<number | 'all'>('all');
  const [refreshing, setRefreshing] = useState(false);

  // Manual refresh function
  const handleManualRefresh = async () => {
    if (!user?.id) return;
    
    setRefreshing(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Update all data from Firebase (no cookie syncing in admin context)
      const [totalWatchtime, videoStats, videoProgressData] = await Promise.all([
        getUserTotalWatchtimeForDisplay(user.id),
        getUserVideoStatsForDisplay(user.id),
        getUserAllVideoProgress(user.id)
      ]);
      
      setUserTotalWatchtime(totalWatchtime);
      setUserVideoStats(videoStats);
      
      // Transform video progress data with improved progress calculation
      const progressMap: { [videoId: string]: any } = {};
      videoProgressData.forEach(progress => {
        // Use the same calculation as the video page, with fallback for missing duration
        let percentWatched = 0;
        if (progress.duration && progress.duration > 0 && progress.lastPosition >= 0) {
          percentWatched = Math.round((progress.lastPosition / progress.duration) * 100);
        } else if (progress.watchTime && progress.watchTime > 0) {
          // Fallback: estimate progress from watchtime if duration is missing
          // Assume average video length of 10 minutes (600 seconds) for estimation
          const estimatedDuration = 600;
          percentWatched = Math.min(Math.round((progress.watchTime / estimatedDuration) * 100), 100);
        }
        
        progressMap[progress.videoId] = {
          percentWatched: percentWatched,
          viewCount: progress.viewCount || 0,
          watchtime: progress.watchTime || 0,
          completed: progress.completed || false,
          lastPosition: progress.lastPosition || 0,
          duration: progress.duration || 0
        };
      });
      
      setUserVideoProgress(progressMap);
      
      setSuccess('Data refreshed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error during manual refresh:', error);
      setError('Failed to refresh data: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setTimeout(() => setError(null), 5000);
    } finally {
      setRefreshing(false);
    }
  };

  // Get all modules and difficulties from videos
  const moduleOptions = Array.from(new Set(videos.map(v => v.module))).sort((a, b) => a - b);
  const difficultyOptions = Array.from(new Set(videos.map(v => v.difficulty).filter(Boolean)));

  // Filter videos by selected module, difficulty, and video number
  let filteredVideos = videos;
  if (selectedModule !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.module === selectedModule);
  }
  if (selectedDifficulty !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.difficulty === selectedDifficulty);
  }
  // Only show video number dropdown if a module is selected
  const videoNumberOptions = selectedModule !== 'all'
    ? Array.from(new Set(videos.filter(v => v.module === selectedModule).map(v => v.moduleOrder))).sort((a, b) => (a ?? 0) - (b ?? 0))
    : [];
  if (selectedVideoNumber !== 'all' && selectedModule !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.moduleOrder === selectedVideoNumber);
  }
  // Sort by module, then moduleOrder
  filteredVideos = filteredVideos.sort((a, b) => {
    if (a.module !== b.module) return a.module - b.module;
    return (a.moduleOrder ?? 0) - (b.moduleOrder ?? 0);
  });

  // Group filtered videos by type (same as before)
  const groupedVideos: { [key: string]: any[] } = {};
  filteredVideos.forEach((v) => {
    if (v.isIntro) {
      groupedVideos['Intro'] = groupedVideos['Intro'] || [];
      groupedVideos['Intro'].push(v);
    } else if (v.isFree) {
      groupedVideos['Free'] = groupedVideos['Free'] || [];
      groupedVideos['Free'].push(v);
    } else if (typeof v.module === 'number') {
      const label = `Modulo ${v.module}`;
      groupedVideos[label] = groupedVideos[label] || [];
      groupedVideos[label].push(v);
    } else {
      groupedVideos['Other'] = groupedVideos['Other'] || [];
      groupedVideos['Other'].push(v);
    }
  });
  const groupOrder = ['Intro', 'Free', ...Object.keys(groupedVideos).filter(k => k.startsWith('Modulo ')).sort((a, b) => {
    const nA = parseInt(a.replace('Modulo ', ''));
    const nB = parseInt(b.replace('Modulo ', ''));
    return nA - nB;
  }), 'Other'];

  useEffect(() => {
    if (user) {
      setEditedUser({ ...user });
      // Load user subscription data
      const loadSubscription = async () => {
        if (user.id) {
          try {
            const userDoc = await getDoc(doc(db, 'users', user.id));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              setUserSubscription(userData.subscription || null);
            }
          } catch (err) {
            console.error('Error loading subscription:', err);
          }
        }
      };
      loadSubscription();
    }
  }, [user]);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const snap = await getDocs(collection(db, 'videos'));
        setVideos(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error('Error fetching videos:', err);
      }
    };
    
    if (open) {
      fetchVideos();
    }
  }, [open]);

  useEffect(() => {
    if (user && user.id) {
      const statusRef = rtdbRef(rtdb, `/status/${user.id}`);
      const unsubscribe = onValue(statusRef, (snapshot) => {
        setOnlineStatus(snapshot.val() === 'online' ? 'online' : 'offline');
      });
      return () => unsubscribe();
    } else {
      setOnlineStatus(null);
    }
  }, [user]);

  const handleSave = async () => {
    if (!editedUser || !user) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Update all user documents with the same email
      const userDocs = await getDocs(collection(db, 'users'));
      const updatePromises = userDocs.docs
        .filter(doc => doc.data().email === user.email)
        .map(doc => {
          // Only include fields that are not undefined
          const updateData: any = {};
          if (editedUser.name !== undefined) updateData.name = editedUser.name;
          if (editedUser.displayName !== undefined) updateData.displayName = editedUser.displayName;
          if (editedUser.role !== undefined) updateData.role = editedUser.role || 'user';
          if (editedUser.isActive !== undefined) updateData.isActive = editedUser.isActive;
          if (editedUser.subscription !== undefined) updateData.subscription = editedUser.subscription;
          return updateDoc(doc.ref, updateData);
        });
      
      await Promise.all(updatePromises);
      setSuccess(t('admin.userUpdatedSuccessfully'));
      setEditing(false);
      
      // Update the parent component's user list
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(t('admin.failedToUpdateUser'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    
    if (!window.confirm(t('admin.deleteUserConfirm', { name: user.name || user.email }))) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Delete all user documents with the same email
      const userDocs = await getDocs(collection(db, 'users'));
      const deletePromises = userDocs.docs
        .filter(doc => doc.data().email === user.email)
        .map(doc => deleteDoc(doc.ref));
      
      await Promise.all(deletePromises);
      setSuccess(t('admin.userDeletedSuccessfully'));
      
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(t('admin.failedToDeleteUser'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setEditedUser(user ? { ...user } : null);
    setError(null);
    setSuccess(null);
  };

  const handleGrantSubscription = () => {
    // Show confirmation dialog first
    if (!user?.id || !currentUser?.uid) {
      setError('User ID or current user not found');
      return;
    }

    if (grantMonths < 1 || grantMonths > 12) {
      setError('Months must be between 1 and 12');
      return;
    }

    setConfirmDialogOpen(true);
  };

  const handleConfirmGrant = async () => {
    if (!user?.id || !currentUser?.uid) {
      setError('User ID or current user not found');
      setConfirmDialogOpen(false);
      return;
    }

    setConfirmDialogOpen(false);
    setGrantingSubscription(true);
    setError(null);
    setSuccess(null);

    try {
      await grantSubscription(
        user.id,
        grantMonths,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Admin'
      );

      // Reload subscription data
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserSubscription(userData.subscription || null);
        setEditedUser({ ...user, subscription: userData.subscription });
      }

      setSuccess(`Successfully granted ${grantMonths} month${grantMonths > 1 ? 's' : ''} subscription`);
      setGrantMonths(1);
    } catch (err: any) {
      setError(err.message || 'Failed to grant subscription');
    } finally {
      setGrantingSubscription(false);
    }
  };

  const handleCancelGrant = () => {
    setConfirmDialogOpen(false);
  };

  const formatSubscriptionDate = (date: any) => {
    if (!date) return 'N/A';
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString();
    }
    if (typeof date === 'string') {
      const d = new Date(date);
      return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
    }
    return 'N/A';
  };

  // Load user's total watchtime when dialog opens
  useEffect(() => {
    const loadUserWatchtime = async () => {
      if (user?.id) {
        try {
          // Get the data directly from Firebase (no cookie syncing in admin context)
          const totalWatchtime = await getUserTotalWatchtimeForDisplay(user.id);
          setUserTotalWatchtime(totalWatchtime);
        } catch (error) {
          console.error('Error loading user watchtime:', error);
        }
      }
    };
    
    if (open && user?.id) {
      loadUserWatchtime();
    }
  }, [open, user?.id]);

  // Load user's video stats when dialog opens
  useEffect(() => {
    const loadUserVideoStats = async () => {
      if (user?.id) {
        try {
          // Get the data directly from Firebase (no cookie syncing in admin context)
          const videoStats = await getUserVideoStatsForDisplay(user.id);
          setUserVideoStats(videoStats);
        } catch (error) {
          console.error('Error loading user video stats:', error);
        }
      }
    };
    
    if (open && user?.id) {
      loadUserVideoStats();
    }
  }, [open, user?.id]);

  // Load user's video progress data when dialog opens
  useEffect(() => {
    const loadUserVideoProgress = async () => {
      if (user?.id) {
        try {
          setProgressLoading(true);
          // Get the data directly from Firebase (no cookie syncing in admin context)
          const videoProgressData = await getUserAllVideoProgress(user.id);
          
          // Transform the data to match the expected format
          const progressMap: { [videoId: string]: any } = {};
          videoProgressData.forEach(progress => {
            // Use the same calculation as the video page, with fallback for missing duration
            let percentWatched = 0;
            if (progress.duration && progress.duration > 0 && progress.lastPosition >= 0) {
              percentWatched = Math.round((progress.lastPosition / progress.duration) * 100);
            } else if (progress.watchTime && progress.watchTime > 0) {
              // Fallback: estimate progress from watchtime if duration is missing
              // Assume average video length of 10 minutes (600 seconds) for estimation
              const estimatedDuration = 600;
              percentWatched = Math.min(Math.round((progress.watchTime / estimatedDuration) * 100), 100);
            }
            
            progressMap[progress.videoId] = {
              percentWatched: percentWatched,
              viewCount: progress.viewCount || 0,
              watchtime: progress.watchTime || 0,
              completed: progress.completed || false,
              lastPosition: progress.lastPosition || 0,
              duration: progress.duration || 0,
              hasStarted: progress.hasStarted || false
            };
          });
          setUserVideoProgress(progressMap);
        } catch (error) {
          console.error('Error loading user video progress:', error);
        } finally {
          setProgressLoading(false);
        }
      }
    };
    
    if (open && user?.id) {
      loadUserVideoProgress();
    }
  }, [open, user?.id]);

  // Set up 30-minute interval to update data (less frequent since it's not critical)
  useEffect(() => {
    if (!open || !user?.id) return;

    const updateData = async () => {
      try {
        // Update all data from Firebase (no cookie syncing in admin context)
        const [totalWatchtime, videoStats, videoProgressData] = await Promise.all([
          getUserTotalWatchtimeForDisplay(user.id),
          getUserVideoStatsForDisplay(user.id),
          getUserAllVideoProgress(user.id)
        ]);
        
        setUserTotalWatchtime(totalWatchtime);
        setUserVideoStats(videoStats);
        
        // Transform video progress data with improved progress calculation
        const progressMap: { [videoId: string]: any } = {};
        videoProgressData.forEach(progress => {
          // Use the same calculation as the video page, with fallback for missing duration
          let percentWatched = 0;
          if (progress.duration && progress.duration > 0 && progress.lastPosition >= 0) {
            percentWatched = Math.round((progress.lastPosition / progress.duration) * 100);
          } else if (progress.watchTime && progress.watchTime > 0) {
            // Fallback: estimate progress from watchtime if duration is missing
            // Assume average video length of 10 minutes (600 seconds) for estimation
            const estimatedDuration = 600;
            percentWatched = Math.min(Math.round((progress.watchTime / estimatedDuration) * 100), 100);
          }
          
          progressMap[progress.videoId] = {
            percentWatched: percentWatched,
            viewCount: progress.viewCount || 0,
            watchtime: progress.watchTime || 0,
            completed: progress.completed || false,
            lastPosition: progress.lastPosition || 0,
            duration: progress.duration || 0
          };
        });
        setUserVideoProgress(progressMap);
        
      } catch (error) {
        console.error('Error updating user data:', error);
      }
    };

    // Update immediately
    updateData();
    
    // Set up 30-minute interval (1800000ms) - less frequent since it's not critical
    const interval = setInterval(updateData, 1800000);
    
    return () => {
      clearInterval(interval);
    };
  }, [open, user?.id]);

  if (!user) return null;

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    if (typeof date === 'string') {
      const d = new Date(date);
      return isNaN(d.getTime()) ? 'Invalid Date' : d.toLocaleString();
    }
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleString();
    }
    return 'Invalid Date';
  };

  const getVideoProgress = (videoId: string) => {
    return userVideoProgress[videoId] || null;
  };

  const formatWatchtime = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.floor(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    }
  };

  const getTotalWatchTime = () => {
    return userTotalWatchtime;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('admin.userManagementDialog')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* User Basic Info */}
          <Box sx={{ flex: { md: '0 0 33%' } }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                  <Avatar 
                    src={user.photoURL} 
                    sx={{ width: 80, height: 80, mb: 2 }}
                  />
                  <Typography variant="h6" align="center">
                    {user.name || user.displayName || user.email}
                  </Typography>
                  <Chip 
                    label={user.role || t('admin.user')} 
                    color={user.role === 'admin' ? 'error' : 'default'}
                    sx={{ mt: 1 }}
                  />
                </Box>

                {editing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label={t('admin.name')}
                      value={editedUser?.name || ''}
                      onChange={(e) => setEditedUser(prev => prev ? { ...prev, name: e.target.value } : null)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label={t('admin.displayName')}
                      value={editedUser?.displayName || ''}
                      onChange={(e) => setEditedUser(prev => prev ? { ...prev, displayName: e.target.value } : null)}
                      fullWidth
                      size="small"
                    />
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('admin.role')}</InputLabel>
                      <Select
                        value={editedUser?.role || 'user'}
                        label={t('admin.role')}
                        onChange={(e) => setEditedUser(prev => prev ? { ...prev, role: e.target.value } : null)}
                      >
                        <MenuItem value="user">{t('admin.user')}</MenuItem>
                        <MenuItem value="admin">{t('admin.admin')}</MenuItem>
                        <MenuItem value="moderator">{t('admin.moderator')}</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editedUser?.isActive ?? true}
                          onChange={(e) => setEditedUser(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
                        />
                      }
                      label={t('admin.activeAccount')}
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>{t('admin.email')}:</strong> {user.email}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('admin.name')}:</strong> {user.name || t('admin.notSet')}
                    </Typography>
                    {/* Status row: use Box to avoid <div> in <p> */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" component="span">
                        <strong>{t('admin.status')}:</strong>
                      </Typography>
                      {onlineStatus && (
                        <Chip
                          label={onlineStatus === 'online' ? t('common.online') : t('common.offline')}
                          color={onlineStatus === 'online' ? 'success' : 'default'}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2">
                      <strong>{t('admin.created')}:</strong> {formatDate(user.createdAt)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>{t('admin.lastLogin')}:</strong> {formatDate(user.lastLogin)}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* User Stats */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>{t('admin.activityStatistics')}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {user.platformLogins?.video || 0}
                      </Typography>
                      <Typography variant="body2">{t('admin.logins')}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
                      <Typography variant="h4" color="secondary">
                        {userVideoStats.completed}
                      </Typography>
                      <Typography variant="body2">{t('admin.completedVideos')}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {formatWatchtime(getTotalWatchTime())}
                      </Typography>
                      <Typography variant="body2">{t('admin.totalWatchTime')}</Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 150px', textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main">
                        {userVideoStats.started}
                      </Typography>
                      <Typography variant="body2">{t('admin.videosStarted')}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Subscription Management - Only for admins/trainers */}
              {isAdminOrTrainer && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Subscription Management</Typography>
                    
                    {/* Current Subscription Info */}
                    {userSubscription && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Current Subscription:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2">
                            <strong>Status:</strong>{' '}
                            <Chip 
                              label={userSubscription.active ? 'Active' : 'Inactive'} 
                              color={userSubscription.active ? 'success' : 'default'}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Typography>
                          <Typography variant="body2">
                            <strong>Plan:</strong> {userSubscription.plan || 'N/A'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>End Date:</strong> {formatSubscriptionDate(userSubscription.endDate)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Unlocked Modules:</strong>{' '}
                            {userSubscription.unlockedModules?.length > 0 
                              ? userSubscription.unlockedModules.join(', ') 
                              : 'None'}
                          </Typography>
                          {userSubscription.history && userSubscription.history.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                History:
                              </Typography>
                              {userSubscription.history.slice(-3).reverse().map((entry: any, idx: number) => (
                                <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                  {entry.action === 'granted' ? 'Granted' : entry.action === 'extended' ? 'Extended' : 'Revoked'}{' '}
                                  {entry.months} month{entry.months > 1 ? 's' : ''} by {entry.grantedByName || 'Admin'}
                                  {entry.timestamp?.seconds && 
                                    ` (${new Date(entry.timestamp.seconds * 1000).toLocaleDateString()})`
                                  }
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Grant Subscription Form */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                      <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Months</InputLabel>
                        <Select
                          value={grantMonths}
                          label="Months"
                          onChange={(e) => setGrantMonths(Number(e.target.value))}
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                            <MenuItem key={num} value={num}>
                              {num} month{num > 1 ? 's' : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        startIcon={grantingSubscription ? <CircularProgress size={18} /> : <CardGiftcardIcon />}
                        onClick={handleGrantSubscription}
                        disabled={grantingSubscription || !user?.id}
                        sx={{ bgcolor: '#E53935', '&:hover': { bgcolor: '#C62828' } }}
                      >
                        {userSubscription?.active ? 'Extend Subscription' : 'Grant Subscription'}
                      </Button>
                    </Box>
                    {userSubscription?.active && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        This will extend the existing subscription by {grantMonths} month{grantMonths > 1 ? 's' : ''} and unlock additional modules.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Video Progress */}
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{t('admin.videoProgress')}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {refreshing && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Syncing...
                        </Typography>
                      )}
                      <IconButton 
                        onClick={handleManualRefresh} 
                        disabled={refreshing} 
                        size="small" 
                        title={refreshing ? "Syncing data..." : "Refresh data"}
                        sx={{ 
                          color: refreshing ? 'text.disabled' : 'primary.main',
                          '&:hover': {
                            backgroundColor: refreshing ? 'transparent' : 'primary.light'
                          }
                        }}
                      >
                        {refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('dashboard.moduleFilter')}</Typography>
                      <select value={selectedModule} onChange={e => setSelectedModule(e.target.value === 'all' ? 'all' : Number(e.target.value))} style={{ padding: 6, borderRadius: 4 }}>
                        <option value="all">{t('dashboard.allModules')}</option>
                        {moduleOptions.map(m => <option key={m} value={m}>{t('dashboard.moduleLabel', { number: m })}</option>)}
                      </select>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('dashboard.difficultyFilter')}</Typography>
                      <select value={selectedDifficulty} onChange={e => setSelectedDifficulty(e.target.value)} style={{ padding: 6, borderRadius: 4 }}>
                        <option value="all">{t('dashboard.allDifficulties')}</option>
                        {difficultyOptions.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </Box>
                    {selectedModule !== 'all' && (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('dashboard.videoNumberFilter')}</Typography>
                        <select value={selectedVideoNumber} onChange={e => setSelectedVideoNumber(e.target.value === 'all' ? 'all' : Number(e.target.value))} style={{ padding: 6, borderRadius: 4 }}>
                          <option value="all">{t('dashboard.allVideoNumbers')}</option>
                          {videoNumberOptions.map(n => <option key={n} value={n}>{t('dashboard.videoNumberLabel', { number: n })}</option>)}
                        </select>
                      </Box>
                    )}
                  </Box>
                  <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {progressLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {t('admin.loadingVideoProgress')}
                        </Typography>
                      </Box>
                    ) : Object.keys(userVideoProgress).length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('admin.noVideoProgressFound')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {t('admin.videoProgressNote')}
                        </Typography>
                      </Box>
                    ) : (
                      groupOrder.filter(g => groupedVideos[g] && groupedVideos[g].length > 0).map((group) => (
                        <Box key={group}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5, mt: 1 }}>{group}</Typography>
                          <Divider sx={{ mb: 1 }} />
                          {groupedVideos[group].map(video => {
                            const progress = getVideoProgress(video.id);
                            return (
                              <ListItem key={video.id} divider>
                                <ListItemText
                                  primary={video.title}
                                  secondary={
                                    progress ? (
                                      <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                          <span style={{ color: '#388e3c', fontWeight: 600 }}>
                                            {t('admin.progress')}: {progress.percentWatched.toFixed(1)}%
                                          </span>
                                          <span style={{ color: '#6c757d' }}>
                                            {t('admin.views')}: {progress.viewCount} | {t('admin.watchtime')}: {formatWatchtime(progress.watchtime || 0)}
                                          </span>
                                        </div>
                                        <div style={{ 
                                          width: '100%', 
                                          height: '6px', 
                                          backgroundColor: '#e0e0e0', 
                                          borderRadius: '12px',
                                          overflow: 'hidden'
                                        }}>
                                          <div style={{ 
                                            width: `${progress.percentWatched}%`, 
                                            height: '100%', 
                                            backgroundColor: progress.completed ? '#4CAF50' : '#2196F3',
                                            transition: 'width 0.3s ease'
                                          }} />
                                        </div>
                                      </div>
                                    ) : (
                                      <span style={{ color: '#6c757d', fontStyle: 'italic' }}>
                                        {t('admin.notStarted')}
                                      </span>
                                    )
                                  }
                                />
                              </ListItem>
                            );
                          })
                        }
                      </Box>
                    ))
                  )}
                  </List>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {editing ? (
          <>
            <Button 
              onClick={handleCancel} 
              startIcon={<CancelIcon />}
              disabled={loading}
            >
              {t('admin.cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained" 
              startIcon={loading ? <CircularProgress size={18} /> : <SaveIcon />}
              disabled={loading}
            >
              {t('admin.saveChanges')}
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={() => setEditing(true)} 
              startIcon={<EditIcon />}
              variant="outlined"
            >
              {t('admin.editUser')}
            </Button>
            <Button 
              onClick={handleDelete} 
              startIcon={<DeleteIcon />}
              color="error"
              variant="outlined"
              disabled={loading}
            >
              {t('admin.deleteUser')}
            </Button>
          </>
        )}
      </DialogActions>

      {/* Confirmation Dialog for Subscription Granting */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelGrant}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff', fontWeight: 600 }}>
          {userSubscription?.active ? 'Extend Subscription?' : 'Grant Subscription?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
            {userSubscription?.active ? (
              <>
                Are you sure you want to extend the subscription for <strong>{user?.name || user?.email}</strong> by{' '}
                <strong>{grantMonths} month{grantMonths > 1 ? 's' : ''}</strong>?
                <br />
                <br />
                This will add {grantMonths} month{grantMonths > 1 ? 's' : ''} to the current expiration date and unlock additional modules.
              </>
            ) : (
              <>
                Are you sure you want to grant a <strong>{grantMonths} month{grantMonths > 1 ? 's' : ''}</strong> subscription to{' '}
                <strong>{user?.name || user?.email}</strong>?
                <br />
                <br />
                This will grant access to modules 1-{grantMonths} and create a purchase record.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCancelGrant}
            variant="outlined"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmGrant}
            variant="contained"
            disabled={grantingSubscription}
            startIcon={grantingSubscription ? <CircularProgress size={18} /> : null}
            sx={{
              bgcolor: '#E53935',
              '&:hover': { bgcolor: '#C62828' },
              '&:disabled': {
                bgcolor: 'rgba(229, 57, 53, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            {grantingSubscription ? 'Granting...' : userSubscription?.active ? 'Extend' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UserManagementDialog; 